import React, { useState, useEffect, useRef } from "react";
import { Check, ChevronsUpDown, Loader2, Save } from "lucide-react";
import { cn } from "../ui/utils";
import { Button } from "../ui/button";
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
    CommandSeparator,
} from "../ui/command";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "../ui/popover";
import { useAuth } from "../../context/AuthContext";
import { Checkbox } from "../ui/checkbox";
import { Label } from "../ui/label";

interface TaskSelectorProps {
    value: string;
    onChange: (value: string) => void;
    isQuickSave: boolean;
    onQuickSaveChange: (val: boolean) => void;
}

interface Item {
    id: string | number;
    name: string;
    type: "Procedure" | "Setting" | "Manual";
    category?: string;
}

export function TaskSelector({ value, onChange, isQuickSave, onQuickSaveChange }: TaskSelectorProps) {
    const [open, setOpen] = useState(false);
    const { token } = useAuth();
    const [items, setItems] = useState<Item[]>([]);
    const [loading, setLoading] = useState(false);
    const [searchValue, setSearchValue] = useState("");

    useEffect(() => {
        const fetchAll = async () => {
            try {
                setLoading(true);
                // Fetch Procedures
                const procRes = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001'}/api/procedures`, {
                    headers: { "Authorization": `Bearer ${token}` }
                });
                const procData = await procRes.json();

                // Fetch Settings
                const setRes = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001'}/api/delegation-settings`, {
                    headers: { "Authorization": `Bearer ${token}` }
                });
                const setData = await setRes.json();

                const allItems: Item[] = [];

                if (procData.success) {
                    procData.procedures.forEach((p: any) => {
                        allItems.push({ id: `p-${p.id}`, name: p.procedureName, type: "Procedure", category: p.department?.departmentName });
                    });
                }

                if (setData.success) {
                    setData.settings.forEach((s: any) => {
                        allItems.push({ id: `s-${s.id}`, name: s.name, type: "Setting", category: s.category });
                    });
                }

                setItems(allItems);
            } catch (error) {
                console.error("Error fetching tasks items:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchAll();
    }, [token]);

    const handleSelect = (currentValue: string) => {
        onChange(currentValue);
        setOpen(false);
    };

    const handleQuickSave = async () => {
        if (!value) return;
        try {
            await fetch(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001'}/api/delegation-settings`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
                body: JSON.stringify({ name: value, type: "Task" })
            });
            // Refresh items (optional but good)
        } catch (error) {
            console.error("Quick save failed", error);
        }
    };

    // Filter items based on search
    const filteredItems = items.filter(item =>
        item.name.toLowerCase().includes(searchValue.toLowerCase())
    );

    const isExisting = items.some(i => i.name === value);

    return (
        <div className="space-y-2">
            <Popover open={open} onOpenChange={setOpen} modal={false}>
                <PopoverTrigger asChild>
                    <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={open}
                        className="w-full justify-between font-normal"
                    >
                        {value ? value : "Select or type task..."}
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                </PopoverTrigger>
                <PopoverContent
                    className="w-[var(--radix-popover-trigger-width)] p-0"
                    onWheel={(e) => e.stopPropagation()}
                >
                    <Command shouldFilter={false}>
                        <CommandInput
                            placeholder="Search or type new task..."
                            value={searchValue}
                            onValueChange={(val) => {
                                setSearchValue(val);
                                // If no exact match, we allow it as manual input
                                if (!items.some(i => i.name === val)) {
                                    // onChange(val); // Don't change immediately to avoid jarring ui if they are just searching
                                }
                            }}
                            className="border-none focus:ring-0 focus-visible:ring-0 outline-none"
                        />
                        <CommandList
                            className="max-h-[300px] overflow-y-auto overflow-x-hidden"
                            onWheel={(e) => e.stopPropagation()}
                        >
                            {loading && <div className="p-4 flex justify-center"><Loader2 className="h-4 w-4 animate-spin" /></div>}

                            {!loading && searchValue && !items.some(i => i.name.toLowerCase() === searchValue.toLowerCase()) && (
                                <CommandGroup heading="New Task">
                                    <CommandItem
                                        value={searchValue}
                                        onSelect={() => handleSelect(searchValue)}
                                    >
                                        <Plus className="mr-2 h-4 w-4" />
                                        Use "{searchValue}"
                                    </CommandItem>
                                </CommandGroup>
                            )}

                            <CommandGroup heading="Procedures">
                                {filteredItems.filter(i => i.type === "Procedure").map((item) => (
                                    <CommandItem
                                        key={item.id}
                                        value={item.name}
                                        onSelect={() => handleSelect(item.name)}
                                    >
                                        <Check
                                            className={cn(
                                                "mr-2 h-4 w-4",
                                                value === item.name ? "opacity-100" : "opacity-0"
                                            )}
                                        />
                                        <div className="flex flex-col">
                                            <span>{item.name}</span>
                                            {item.category && <span className="text-xs text-muted-foreground">{item.category}</span>}
                                        </div>
                                    </CommandItem>
                                ))}
                            </CommandGroup>

                            <CommandSeparator />

                            <CommandGroup heading="Predefined List">
                                {filteredItems.filter(i => i.type === "Setting").map((item) => (
                                    <CommandItem
                                        key={item.id}
                                        value={item.name}
                                        onSelect={() => handleSelect(item.name)}
                                    >
                                        <Check
                                            className={cn(
                                                "mr-2 h-4 w-4",
                                                value === item.name ? "opacity-100" : "opacity-0"
                                            )}
                                        />
                                        <span>{item.name}</span>
                                    </CommandItem>
                                ))}
                            </CommandGroup>
                        </CommandList>
                    </Command>
                </PopoverContent>
            </Popover>

            {value && !isExisting && (
                <div className="flex items-center space-x-2 p-2 bg-slate-50 dark:bg-slate-900 rounded border border-dashed">
                    <Checkbox
                        id="quicksave"
                        checked={isQuickSave}
                        onCheckedChange={(checked) => onQuickSaveChange(!!checked)}
                    />
                    <Label htmlFor="quicksave" className="text-xs font-normal cursor-pointer flex items-center gap-1">
                        <Save size={12} /> Quick save to Predefined List
                    </Label>
                </div>
            )}

            {/* Hidden Input to handle the actual value if needed, but we pass value/onChange props */}
            <input type="hidden" name="quicksave_active" value={isQuickSave ? "true" : "false"} id="quicksave_active" />
        </div>
    );
}

const Plus = ({ className }: { className?: string }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M5 12h14" /><path d="M12 5v14" /></svg>
);
